<template>
    <div class="pa-3">
        <v-toolbar dense rounded="">
            <v-toolbar-title>
                <strong class="mr-3 primary--text">{{ hr ? 'Calendar — all staff' : 'Calendar' }}</strong>
            </v-toolbar-title>
            <v-btn icon small @click="shift(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
            <span class="subtitle-2 mx-1" style="min-width: 120px; text-align: center;">{{ monthLabel(shown) }}</span>
            <v-btn icon small @click="shift(1)"><v-icon>mdi-chevron-right</v-icon></v-btn>
            <v-btn v-if="meta && !atHome" small text color="primary" @click="goHome">{{ planning ? 'Next month' : 'This month' }}</v-btn>
            <v-spacer></v-spacer>
            <v-icon @click="reload">mdi-refresh</v-icon>
        </v-toolbar>

        <v-alert v-if="!hr && meta && meta.self_entry_open" type="info" dense text class="mt-3 mb-0" icon="mdi-calendar-month-outline">
            Agree your plans for {{ monthLabel(meta.next) }} (WFH days, annual leave …) with your section head, enter them under
            <strong>My schedule</strong> and send each one for approval by <strong>{{ fmtFullDate(meta.deadline) }}</strong>.
        </v-alert>

        <v-card class="mt-3" v-if="meta">
            <v-tabs v-model="tab" height="40">
                <v-tab v-for="t in tabs" :key="t">{{ TAB_LABELS[t] }}</v-tab>
            </v-tabs>
            <v-divider></v-divider>
            <v-tabs-items v-model="tab">
                <v-tab-item v-for="t in tabs" :key="t">
                    <div class="pa-3">
                        <shared-calendar v-if="t === 'calendar'" :key="'cal' + version + '-' + calVersion" :year="view.year" :month="view.month"
                            :all-staff="hr" :my-section-id="hr ? null : meta.section_id" />
                        <month-schedule v-else-if="t === 'mine'" :key="'my' + version" :year="plan.year" :month="plan.month" @changed="calVersion++" />
                        <team-review v-else-if="t === 'team'" :key="'team' + version" :year="plan.year" :month="plan.month" @changed="calVersion++" />
                        <hr-planner v-else-if="t === 'planner'" :key="'planner' + version" :year="plan.year" :month="plan.month" @changed="calVersion++" />
                        <approved-wfh v-else-if="t === 'wfh'" :key="'wfh' + version" :year="view.year" :month="view.month" />
                    </div>
                </v-tab-item>
            </v-tabs-items>
        </v-card>
        <v-progress-linear v-else indeterminate class="mt-3"></v-progress-linear>
    </div>
</template>

<script>
// Monthly schedule. Staff menu (/calendar): the calendar of my section, my
// month (each plan sent for approval) and the section head's plans to
// approve. HR Staff menu (/hrms/calendar, `hr`): the calendar of all staff,
// HR's planner (HrPlanner: plans painted on many rows, saved at once with a
// reason) and the approved WFH list. Entry types are backend config.
// The calendar and approved WFH open on this month, planning (my schedule,
// my team, planner) on next month; each keeps its own month.
// Backend: MonthlyScheduleController.
import SharedCalendar from './SharedCalendar.vue'
import MonthSchedule from './MonthSchedule.vue'
import TeamReview from './TeamReview.vue'
import ApprovedWfh from './ApprovedWfh.vue'
import HrPlanner from './HrPlanner.vue'

const TAB_LABELS = { calendar: 'Calendar', mine: 'My schedule', team: 'My team', planner: 'Planner', wfh: 'Approved WFH' }
const PLANNING = ['mine', 'team', 'planner']

export default {
    name: 'MonthlyCalendar',
    components: { SharedCalendar, MonthSchedule, TeamReview, ApprovedWfh, HrPlanner },
    props: {
        hr: { type: Boolean, default: false },
    },
    data: () => ({
        TAB_LABELS,
        meta: null,
        view: null, // { year, month } of the calendar and approved WFH
        plan: null, // { year, month } of the planning tabs
        tab: 0, // index into tabs; always opens on the calendar
        version: 0,
        calVersion: 0, // redraw the shared calendar after a plan changed
    }),
    watch: {
        // Switching between the two menu items can reuse this page.
        hr() {
            this.tab = 0
            this.version++
        },
    },
    computed: {
        tabs() {
            if (!this.meta) return []
            return this.hr ? ['calendar', 'planner', 'wfh'] : ['calendar', 'mine', ...(this.meta.is_approver ? ['team'] : [])]
        },
        planning() {
            return PLANNING.includes(this.tabs[this.tab])
        },
        shown() {
            return this.planning ? this.plan : this.view
        },
        home() {
            return this.meta && (this.planning ? this.meta.next : this.meta.current)
        },
        atHome() {
            return this.shown && this.shown.year === this.home.year && this.shown.month === this.home.month
        },
    },
    mounted() {
        this.$axios.get('/api/monthly-schedule/meta').then(resp => {
            this.meta = resp.data
            this.view = { ...resp.data.current }
            this.plan = { ...resp.data.next }
        })
    },
    methods: {
        shift(delta) {
            const d = new Date(this.shown.year, this.shown.month - 1 + delta, 1)
            this.setShown({ year: d.getFullYear(), month: d.getMonth() + 1 })
        },
        goHome() {
            this.setShown({ ...this.home })
        },
        setShown(m) {
            if (this.planning) this.plan = m
            else this.view = m
        },
        monthLabel(m) {
            if (!m) return ''
            return new Date(m.year, m.month - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
        },
        reload() {
            this.version++
        },
        fmtFullDate(s) {
            const [y, m, d] = s.split('-')
            return `${d}/${m}/${y}`
        },
    },
}
</script>
